/**
 * LinkedIn URL Helper Functions
 * Handles normalization of LinkedIn URLs from various sources
 */

/**
 * Normalize LinkedIn URLs to public profile format
 * Handles Sales Navigator URLs, public URLs, and extracts profile IDs
 */
export function normalizeLinkedInUrl(url: string): { 
  publicUrl: string; 
  profileId: string | null;
  urlType: 'public' | 'sales_nav' | 'unknown';
} {
  if (!url) {
    return { publicUrl: url, profileId: null, urlType: 'unknown' };
  }
  
  // Sales Navigator URL pattern:
  // https://linkedin.com/sales/people/ACwAAAm__oMBrdr2cueJOjuVQPyOilJwc_HsLyY,NAME_SEARCH,Gj7s
  // Note: The extracted ID is a provider_id, not a vanity URL.
  // publicUrl is for display/dedup only - backend uses provider_id for API calls.
  const salesNavMatch = url.match(/linkedin\.com\/sales\/people\/([^,]+)/);
  if (salesNavMatch) {
    const profileId = salesNavMatch[1];
    return {
      publicUrl: `https://www.linkedin.com/in/${profileId}`,
      profileId: profileId,
      urlType: 'sales_nav'
    };
  }
  
  // Public URL pattern:
  // https://www.linkedin.com/in/john-doe-123456/
  const publicMatch = url.match(/linkedin\.com\/in\/([^\/\?]+)/);
  if (publicMatch) {
    const vanityName = publicMatch[1];
    return {
      publicUrl: `https://www.linkedin.com/in/${vanityName}`,
      profileId: null,  // Vanity names don't have IDs
      urlType: 'public'
    };
  }
  
  // Unknown format, return as-is
  return { publicUrl: url, profileId: null, urlType: 'unknown' };
}

/**
 * Normalize a LinkedIn URL to a canonical key for deduplication.
 * 
 * Handles Sales Navigator URLs (/sales/lead/, /sales/people/) by extracting 
 * the core member ID before any commas or query params. Handles public URLs 
 * (/in/vanity-name) by extracting the vanity slug.
 * 
 * Returns a lowercase canonical string like:
 *   "salesnav:acwaab0n5co..."  or  "profile:john-doe"  or  "raw:<normalized-url>"
 */
export function normalizeLinkedInUrlForDedup(url: string | null): string {
  if (!url) return "";

  const trimmed = url.trim();
  if (!trimmed) return "";

  // Strip query parameters and hash first
  const withoutQuery = trimmed.split('?')[0].split('#')[0];

  // Sales Navigator /sales/lead/ pattern
  const salesLeadMatch = withoutQuery.match(/linkedin\.com\/sales\/lead\/([^,\/]+)/i);
  if (salesLeadMatch) {
    return `memberid:${salesLeadMatch[1].toLowerCase()}`;
  }

  // Sales Navigator /sales/people/ pattern
  const salesPeopleMatch = withoutQuery.match(/linkedin\.com\/sales\/people\/([^,\/]+)/i);
  if (salesPeopleMatch) {
    return `memberid:${salesPeopleMatch[1].toLowerCase()}`;
  }

  // Public URL /in/ pattern
  const publicMatch = withoutQuery.match(/linkedin\.com\/in\/([^\/]+)/i);
  if (publicMatch) {
    const slug = publicMatch[1];
    // ACw-prefixed slugs are actually member IDs (from Sales Nav), not vanity names
    if (/^ACw/i.test(slug)) {
      const memberId = slug.split(',')[0]; // strip trailing params
      return `memberid:${memberId.toLowerCase()}`;
    }
    return `profile:${slug.toLowerCase()}`;
  }

  // Fallback: basic normalization
  return `raw:${withoutQuery.toLowerCase().replace(/\/$/, "")}`;
}

/**
 * Extract the LinkedIn member ID (ACw... prefix) from a Sales Navigator URL.
 * Returns null for non-Sales Nav URLs.
 */
export function extractSalesNavMemberId(url: string | null): string | null {
  if (!url) return null;
  const trimmed = url.trim();
  const withoutQuery = trimmed.split('?')[0].split('#')[0];

  const leadMatch = withoutQuery.match(/linkedin\.com\/sales\/lead\/([^,\/]+)/i);
  if (leadMatch) return leadMatch[1].toLowerCase();

  const peopleMatch = withoutQuery.match(/linkedin\.com\/sales\/people\/([^,\/]+)/i);
  if (peopleMatch) return peopleMatch[1].toLowerCase();

  return null;
}

/**
 * Check if two LinkedIn URLs refer to the same person using provider_id as a bridge.
 * When one URL is Sales Nav (has member ID) and the other is public (vanity slug),
 * URL-based dedup cannot match them. This function enables a secondary dedup layer
 * using the linkedin_profile_id / provider_id stored on existing targets.
 */
export function buildProfileIdDedupSet(
  existingTargets: { profile_url: string | null; linkedin_profile_id?: string | null; provider_id?: string | null }[]
): Set<string> {
  const ids = new Set<string>();
  for (const t of existingTargets) {
    const pid = t.linkedin_profile_id || t.provider_id;
    if (pid) ids.add(pid.toLowerCase());
    // Also add Sales Nav member IDs extracted from URLs
    const memberId = extractSalesNavMemberId(t.profile_url);
    if (memberId) ids.add(memberId);
  }
  return ids;
}
